// ============================================================
// Compound intent parser.
//
// Detects queries that combine a CATEGORY (what) with a
// LOCATION (where), and synthesizes a single best-answer
// result card that surfaces at the top of the search dropdown.
//
// Examples it handles:
//   "home builder in munno para"   → "Home builders in Munno Para"
//   "builders munno para"          → same (order-agnostic)
//   "verandah in greenacres"       → "Verandahs in Greenacres"
//   "knockdown rebuild magill"     → "Knockdown rebuild in Magill"
//   "cost to build mount barker"   → "Cost to build in Mount Barker"
//   "first home buyer gawler"      → "First home builders in Gawler"
//
// Single-suburb queries ("munno para") are NOT handled here  - 
// they're picked up by the regular content-index search.
// ============================================================

import { suburbSeoData } from './suburbSeoData';
import { estateData } from './estateData';
import { regionalData } from './regionalData';
import { ADELAIDE_SUBURBS } from './adelaideSuburbs';

const REGION_ROUTES = {
  'Northern Adelaide':  '/home-builders-northern-adelaide',
  'Southern Adelaide':  '/home-builders-southern-adelaide',
  'Adelaide Hills':     '/home-builders-adelaide-hills',
  'Western Adelaide':   '/home-builders-western-adelaide',
  'Eastern Adelaide':   '/home-builders-near-me-adelaide',
  'Inner Adelaide':     '/home-builders-near-me-adelaide',
};

// Council-area shortcut routes (we have dedicated pages for these).
const COUNCIL_ROUTES = {
  'onkaparinga': '/building-in-onkaparinga-council',
  'playford': '/building-in-playford-council',
  'salisbury': '/building-in-salisbury-council',
  'tea tree gully': '/building-in-tea-tree-gully-council',
  'charles sturt': '/building-in-charles-sturt-council',
  'mount barker council': '/building-in-mount-barker-council',
};

// Category map. Each entry has:
//   keywords   - phrases (lowercased) that signal the category
//   label      - display label used in the result title
//   to         - static destination (overridden when a suburb is
//                detected and `useSuburbPage` is true)
//   useSuburbPage - if true and a suburb is matched, the result
//                routes to /building-in/<slug> instead of `to`
const CATEGORIES = [
  // Most specific first so "first home buyer" beats plain "builder".
  { keywords: ['first home buyer', 'first home', 'fhb', 'fhog'],          label: 'First home buyer support',  to: '/first-home-buyer-adelaide',   useSuburbPage: true },
  { keywords: ['knockdown rebuild', 'knock down rebuild', 'kdr'],         label: 'Knockdown rebuild',          to: '/knockdown-rebuild',            useSuburbPage: true },
  { keywords: ['house and land', 'h&l', 'h and l', 'hl package'],         label: 'House and land',             to: '/house-and-land-packages-adelaide', useSuburbPage: true },
  { keywords: ['custom home', 'architectural home'],                       label: 'Custom home builders',       to: '/custom-home-builders-adelaide', useSuburbPage: true },
  { keywords: ['two storey', 'double storey'],                             label: 'Two-storey home builders',   to: '/best-builder-for-two-storey-homes', useSuburbPage: true },
  { keywords: ['acreage'],                                                 label: 'Acreage home builders',      to: '/best-builder-for-acreage',     useSuburbPage: true },
  { keywords: ['display home', 'display'],                                 label: 'Display homes',              to: '/display-homes-adelaide',       useSuburbPage: true },
  { keywords: ['renovation', 'renovate', 'renovating', 'reno'],            label: 'Renovations',                to: '/renovations',                  useSuburbPage: true },
  { keywords: ['interior design'],                                         label: 'Interior design',            to: '/interior-design',              useSuburbPage: false },
  { keywords: ['home design', 'floor plan', 'designs'],                    label: 'Home designs',               to: '/designs',                      useSuburbPage: false },
  { keywords: ['verandah', 'pergola'],                                     label: 'Verandahs & pergolas',       to: '/verandahs-pergolas',           useSuburbPage: false },
  { keywords: ['landscape', 'landscaping'],                                label: 'Landscaping',                to: '/landscaping',                  useSuburbPage: false },
  { keywords: ['shed', 'garage'],                                          label: 'Sheds & garages',            to: '/sheds-garages',                useSuburbPage: false },
  { keywords: ['decking', 'deck'],                                         label: 'Decking',                    to: '/decking',                      useSuburbPage: false },
  { keywords: ['swimming pool', 'pool'],                                   label: 'Swimming pools',             to: '/swimming-pools',               useSuburbPage: false },
  { keywords: ['cost to build', 'build cost', 'cost', 'price', 'cheap'],   label: 'Cost to build',              to: '/adelaide-build-cost-report-2026', useSuburbPage: true },
  // Catch-all builder intent - must run last so it doesn't beat "first home", "custom home", etc.
  { keywords: ['home builder', 'new home', 'builder', 'build a home', 'build a house', 'building a home'],
                                                                            label: 'Home builders',              to: '/home-builders-adelaide',       useSuburbPage: true },
];

// Build a lookup of suburb names (and aliases) → suburb data.
// We pre-compute this once at module load so each search is O(1).
//
// Two tiers:
//   1. Suburbs in suburbSeoData → routed to /building-in/<slug>  (best)
//   2. Suburbs only in regionalData.coverage → routed to the
//      region page (/home-builders-<region>) as a sensible fallback,
//      with a synthesised "we don't have a dedicated page yet" hint.
const SUBURB_NAMES_SORTED = (() => {
  const arr = [];
  const seen = new Set();

  // Tier 1 - full suburb pages.
  for (const slug of Object.keys(suburbSeoData)) {
    const s = suburbSeoData[slug];
    if (!s || !s.name) continue;
    const lc = s.name.toLowerCase();
    arr.push({ name: lc, data: s, tier: 'full' });
    seen.add(lc);
    const slugAsName = slug.replace(/-/g, ' ');
    if (slugAsName !== lc && !seen.has(slugAsName)) {
      arr.push({ name: slugAsName, data: s, tier: 'full' });
      seen.add(slugAsName);
    }
  }

  // Tier 2 - coverage suburbs from regionalData. These are real
  // Adelaide suburbs that we don't have full pages for yet, but
  // we can still detect them and route to the region landing page.
  for (const regionSlug of Object.keys(regionalData)) {
    const r = regionalData[regionSlug];
    const regionRoute = `/home-builders-${regionSlug}`;
    const regionName = r.name;
    if (!Array.isArray(r.coverage)) continue;
    for (const entry of r.coverage) {
      // Coverage entries can include "&" or be sentence-style
      // ("Gawler & Gawler East"). Split on "&" and trim.
      for (const piece of entry.split(/\s*&\s*/)) {
        // Drop generic suffixes like "Vale" from compound names? No - keep full.
        const lc = piece.trim().toLowerCase();
        if (!lc || seen.has(lc)) continue;
        // Skip very short noise tokens.
        if (lc.length < 4) continue;
        arr.push({
          name: lc,
          data: {
            name: piece.trim(),
            region: regionName,
            slug: lc.replace(/\s+/g, '-'),
            intro: `${piece.trim()} is in ${regionName}. We don't yet have a dedicated suburb page, but you can browse builders covering this area.`,
          },
          tier: 'coverage',
          regionRoute,
        });
        seen.add(lc);
      }
    }
  }

  // Tier 3 - comprehensive Adelaide suburb directory (curated).
  // Picks up suburbs that aren't in suburbSeoData OR regionalData
  // coverage (e.g. Magill, Greenacres, Stirling). Routes to the
  // region's "home builders" page.
  for (const [name, region] of Object.entries(ADELAIDE_SUBURBS)) {
    const lc = name.toLowerCase();
    if (seen.has(lc)) continue;
    const regionRoute = REGION_ROUTES[region] || '/home-builders-adelaide';
    arr.push({
      name: lc,
      data: {
        name,
        region,
        slug: lc.replace(/\s+/g, '-'),
        intro: `${name} is in ${region}. Browse partner builders covering this area or talk to One on One Support for a tailored shortlist.`,
      },
      tier: 'directory',
      regionRoute,
    });
    seen.add(lc);
  }

  // Sort by length descending so multi-word suburbs win.
  arr.sort((a, b) => b.name.length - a.name.length);
  return arr;
})();

// Estates are also valid locations.
const ESTATE_NAMES_SORTED = (() => {
  const arr = [];
  for (const slug of Object.keys(estateData)) {
    const e = estateData[slug];
    if (e && e.name) {
      arr.push({ name: e.name.toLowerCase(), data: e, slug });
    }
  }
  arr.sort((a, b) => b.name.length - a.name.length);
  return arr;
})();

// Returns the longest matching suburb entry (with tier metadata), or null.
const findSuburbIn = (q) => {
  for (const s of SUBURB_NAMES_SORTED) {
    if (q.includes(s.name)) return s;
  }
  return null;
};

// Returns the longest matching estate in the query string, or null.
const findEstateIn = (q) => {
  for (const e of ESTATE_NAMES_SORTED) {
    if (q.includes(e.name)) return e;
  }
  return null;
};

// Returns the matched category, or null.
const findCategoryIn = (q) => {
  for (const c of CATEGORIES) {
    for (const kw of c.keywords) {
      // Phrase must appear at a word boundary. We accept it if the
      // char before is non-alphanumeric (or start) and the char
      // after is non-alphanumeric, end-of-string, OR a single 's'
      // (so "builder" matches "builders", "renovation" matches
      // "renovations", etc.).
      const idx = q.indexOf(kw);
      if (idx === -1) continue;
      const before = idx === 0 ? '' : q[idx - 1];
      const afterStart = idx + kw.length;
      const after = afterStart >= q.length ? '' : q[afterStart];
      const afterAfter = afterStart + 1 >= q.length ? '' : q[afterStart + 1];

      const beforeOk = !before || /[^a-z0-9]/.test(before);
      const afterOk = !after || /[^a-z0-9]/.test(after) ||
        (after === 's' && (!afterAfter || /[^a-z0-9]/.test(afterAfter)));

      if (beforeOk && afterOk) return c;
    }
  }
  return null;
};

// Returns the matched council route, or null.
const findCouncilIn = (q) => {
  for (const name in COUNCIL_ROUTES) {
    if (q.includes(name + ' council') || q.includes('council of ' + name) ||
        (q.includes(name) && q.includes('council'))) {
      return { name, to: COUNCIL_ROUTES[name] };
    }
  }
  return null;
};

// Builds a compound result for [category + suburb].
const buildSuburbCompound = (category, suburbEntry) => {
  const suburb = suburbEntry.data;
  const isFull = suburbEntry.tier === 'full';

  // Non-full tier suburbs (coverage / directory) have no dedicated
  // page, so they route to the region page instead of /building-in/<slug>
  // which would 404.
  let dest;
  if (isFull) {
    dest = category.useSuburbPage
      ? `/building-in/${suburb.slug}`
      : category.to;
  } else {
    dest = category.useSuburbPage
      ? suburbEntry.regionRoute
      : category.to;
  }

  let snippet = '';
  if (category.label === 'Home builders' && Array.isArray(suburb.popularBuilders) && suburb.popularBuilders.length) {
    snippet = `Partner builders active in ${suburb.name}: ${suburb.popularBuilders.slice(0, 4).join(', ')}.`;
  } else if (category.label === 'Cost to build' && Array.isArray(suburb.stats)) {
    const buildCost = suburb.stats.find((s) => /cost/i.test(s.label));
    const land = suburb.stats.find((s) => /land/i.test(s.label));
    snippet = [
      buildCost ? `Build cost ${buildCost.value}` : null,
      land ? `Land ${land.value}` : null,
    ].filter(Boolean).join(' · ');
    if (!snippet) snippet = (suburb.intro || '').slice(0, 180);
  } else if (!isFull) {
    snippet = `${suburb.name} is in ${suburb.region}. Browse partner builders covering ${suburb.region} or talk to One on One Support for a tailored shortlist.`;
  } else {
    snippet = (suburb.intro || suburb.heroSubtitle || '').slice(0, 200);
  }

  return {
    kind: 'compound',
    score: 1000,
    title: `${category.label} in ${suburb.name}`,
    subtitle: `${suburb.region} · ${isFull ? 'synthesised result' : 'region match'}`,
    snippet,
    to: dest,
  };
};

// Builds a compound result for [category + estate].
const buildEstateCompound = (category, estate) => {
  const dest = category.useSuburbPage
    ? `/estates/${estate.slug}`
    : category.to;
  const e = estate.data;
  let snippet = '';
  if (category.label === 'Home builders' && Array.isArray(e.activeBuilders) && e.activeBuilders.length) {
    snippet = `Active in ${e.name}: ${e.activeBuilders.slice(0, 4).join(', ')}.`;
  } else {
    snippet = (e.overview || e.heroSubtitle || '').slice(0, 200);
  }
  return {
    kind: 'compound',
    score: 1000,
    title: `${category.label} in ${e.name} Estate`,
    subtitle: `${e.region || e.location || 'Estate'} · synthesised result`,
    snippet,
    to: dest,
  };
};

const buildCouncilCompound = (category, council) => {
  const titleCase = council.name.replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    kind: 'compound',
    score: 1000,
    title: `${category.label} in ${titleCase} Council`,
    subtitle: 'Council area · synthesised result',
    snippet: `Building guidance for the ${titleCase} council area, including overlays, application process and partner builders that work in this LGA.`,
    to: council.to,
  };
};

/**
 * Parse a free-text query and try to detect a [category + location]
 * compound intent. Returns a synthesized result, or null if nothing
 * compound was detected.
 *
 * @param {string} rawQuery
 * @returns {object | null}
 */
export function parseCompoundIntent(rawQuery) {
  const q = (rawQuery || '').toLowerCase().trim();
  if (!q || q.length < 4) return null;

  const category = findCategoryIn(q);
  if (!category) return null;

  // Order: estate → council → suburb. Estates are most specific.
  const estate = findEstateIn(q);
  if (estate) return buildEstateCompound(category, estate);

  const council = findCouncilIn(q);
  if (council) return buildCouncilCompound(category, council);

  const suburb = findSuburbIn(q);
  if (suburb) return buildSuburbCompound(category, suburb);

  return null;
}
